/* One task: description, checklist, links, attachments, and the log.

   The log is the only history this system keeps, so it is given a panel of its
   own rather than being tucked behind a disclosure. Every write on this page
   appends to it; in demo mode the actor is `demo@localhost` for everybody,
   which is deliberate -- the log should not claim a person was responsible for
   a change made while nobody was signed in. */
import { useCallback, useEffect, useState } from "react";

import { api, type Task } from "../api";
import { DueDate, Field, Panel } from "../components/ui";
import { BetChips, InitiativeChip } from "../components/strategy";
import { usePermissions } from "../auth";
import { go, href } from "../route";
import { useStore } from "../store";

export default function TaskDetail({ id }: { id: number }) {
  const { meta, objects, people, initiatives, run, refresh } = useStore();
  const [task, setTask] = useState<Task | null>(null);
  const [missing, setMissing] = useState(false);
  const [newSubtask, setNewSubtask] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const { canDelete, canWrite } = usePermissions();

  const load = useCallback(async () => {
    try {
      setTask(await api.task(id));
      setMissing(false);
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (missing) return <p className="note">No such task. It may have been deleted.</p>;
  if (!task || !meta) return <p className="note">Loading…</p>;

  const act = async <T,>(action: () => Promise<T>) => {
    const result = await run(action);
    if (result) await load();
  };

  /* Which bets sit behind the initiative this task serves. A lookup, not a
     number, so resolving it from the store cannot disagree with the server. */
  const betsOf = (initiativeId: number) =>
    initiatives.find((i) => i.id === initiativeId)?.bet_ids ?? [];



  return (
    <>
      <p className="crumbs">
        <a className="crumb" href="#/tasks">
          Tasks
        </a>{" "}
        / Task
      </p>
      <div className="panel-hd" style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: "var(--display)", fontSize: 22, margin: 0 }}>{task.title}</h1>
        <span className="muted">
          <DueDate date={task.next_milestone_date} status={task.status} />
        </span>
      </div>

      <div className="detail">
        <div>
          <Panel title="Details">
            <Field label="Title">
              <input
                type="text"
                key={`title-${task.updated_at}`}
                defaultValue={task.title}
                onBlur={(e) =>
                  e.target.value !== task.title && act(() => api.patchTask(id, { title: e.target.value }))
                }
              />
            </Field>
            <Field label="Description">
              <textarea
                key={`desc-${task.updated_at}`}
                defaultValue={task.description}
                onBlur={(e) =>
                  e.target.value !== task.description &&
                  act(() => api.patchTask(id, { description: e.target.value }))
                }
              />
            </Field>
            <div className="row">
              <Field label="Status">
                <select
                  value={task.status}
                  onChange={(e) => act(() => api.patchTask(id, { status: e.target.value }))}
                >
                  {meta.task_statuses.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Owner">
                <select
                  value={task.owner_id ?? ""}
                  onChange={(e) =>
                    act(() =>
                      api.patchTask(id, {
                        owner_id: e.target.value === "" ? null : Number(e.target.value),
                      }),
                    )
                  }
                >
                  <option value="">Unassigned</option>
                  {people.map((p) => (
                    <option key={p.person_id} value={p.person_id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Next milestone">
                <input
                  type="date"
                  key={`date-${task.updated_at}`}
                  defaultValue={task.next_milestone_date ?? ""}
                  onChange={(e) =>
                    act(() =>
                      api.patchTask(id, { next_milestone_date: e.target.value || null }),
                    )
                  }
                />
              </Field>
            </div>
          </Panel>

          <Panel
            title="Checklist"
            aside={
              <span className="muted">
                {task.checklist?.filter((c) => c.is_done).length ?? 0} of{" "}
                {task.checklist?.length ?? 0}
              </span>
            }
          >
            <ul className="checklist">
              {(task.checklist ?? []).map((item) => (
                <li key={item.subtask_id} className={item.is_done ? "done" : undefined}>
                  <input
                    type="checkbox"
                    checked={!!item.is_done}
                    onChange={(e) =>
                      act(() => api.setSubtask(item.subtask_id, e.target.checked))
                    }
                  />
                  <span>{item.text}</span>
                  <button
                    className="linkbtn x"
                    onClick={() => act(() => api.removeSubtask(item.subtask_id))}
                  >
                    remove
                  </button>
                </li>
              ))}
              {(task.checklist ?? []).length === 0 && <li className="muted">nothing yet</li>}
            </ul>
            <div className="row" style={{ marginTop: 10 }}>
              <input
                type="text"
                style={{ flex: 1 }}
                placeholder="add a checklist item…"
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || !newSubtask.trim()) return;
                  const text = newSubtask.trim();
                  setNewSubtask("");
                  void act(() => api.addSubtask(id, text));
                }}
              />
              <button
                className="btn"
                disabled={!newSubtask.trim()}
                onClick={() => {
                  const text = newSubtask.trim();
                  setNewSubtask("");
                  void act(() => api.addSubtask(id, text));
                }}
              >
                Add
              </button>
            </div>
          </Panel>

          <Panel title="Documents">
            <ul className="deps">
              {(task.links ?? []).map((link) => (
                <li key={link.link_id}>
                  <a href={link.url} target="_blank" rel="noreferrer">
                    {link.title || link.url}
                  </a>
                  <button
                    className="linkbtn"
                    style={{ marginLeft: "auto" }}
                    onClick={() => act(() => api.removeLink(link.link_id))}
                  >
                    remove
                  </button>
                </li>
              ))}
              {(task.links ?? []).length === 0 && <li className="muted">no links yet</li>}
            </ul>
            <div className="row" style={{ marginTop: 10 }}>
              <input
                type="text"
                placeholder="https://…"
                style={{ flex: 2 }}
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <input
                type="text"
                placeholder="label (optional)"
                style={{ flex: 1 }}
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
              />
              <button
                className="btn"
                disabled={!linkUrl.trim()}
                onClick={() => {
                  const url = linkUrl.trim();
                  const label = linkTitle.trim();
                  setLinkUrl("");
                  setLinkTitle("");
                  void act(() => api.addLink(id, url, label));
                }}
              >
                Add
              </button>
            </div>
          </Panel>
        </div>

        <div>
          <Panel title="Attached to">
            {/* One object, or none. Changing the selection moves the task
                rather than adding to a set, which is why this is a picker and
                not a list with an add control. */}
            <select
              value={task.attached_to ? `${task.attached_to.kind}:${task.attached_to.id}` : ""}
              disabled={!canWrite}
              style={{ width: "100%" }}
              onChange={(e) => {
                if (!e.target.value) {
                  void act(() => api.patchTask(id, { attached_to: null }));
                  return;
                }
                const [kind, oid] = e.target.value.split(":");
                void act(() =>
                  api.patchTask(id, { attached_to: { kind, id: Number(oid) } }),
                );
              }}
            >
              <option value="">Standalone — attached to nothing</option>
              {objects.map((o) => (
                <option key={`${o.kind}-${o.id}`} value={`${o.kind}:${o.id}`}>
                  {o.name} ({o.kind})
                </option>
              ))}
            </select>
            {task.attached_to && (
              <p className="meta-line" style={{ marginTop: 8 }}>
                <a href={href.object(task.attached_to.kind, task.attached_to.id)}>
                  open {task.attached_to.name}
                </a>
              </p>
            )}
          </Panel>

          <Panel title="Initiative">
            {/* The override, not the answer. Clearing it does not leave the
                task without an initiative -- it falls back to the object's,
                which is why the two states are spelled out rather than left to
                an empty select. */}
            <select
              value={task.initiative_id ?? ""}
              disabled={!canWrite}
              style={{ width: "100%" }}
              onChange={(e) =>
                void act(() =>
                  api.patchTask(id, {
                    initiative_id: e.target.value === "" ? null : Number(e.target.value),
                  }),
                )
              }
            >
              <option value="">
                {task.attached_to
                  ? "Follow the service or asset"
                  : "No initiative"}
              </option>
              {initiatives.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.name}
                </option>
              ))}
            </select>
            <p className="meta-line" style={{ marginTop: 8 }}>
              {task.initiative ? (
                <>
                  <InitiativeChip
                    initiative={task.initiative}
                    source={task.initiative.source}
                  />{" "}
                  {task.initiative.name}
                  {task.initiative.source === "inherited"
                    ? " — inherited from its object"
                    : " — set on this task"}
                  <BetChips betIds={betsOf(task.initiative.id)} />
                </>
              ) : (
                "Not connected to any initiative."
              )}
            </p>
          </Panel>

          <Panel title="Log" aside={<span className="muted">{task.events?.length ?? 0}</span>}>
            <ul className="log">
              {(task.events ?? []).map((event) => (
                <li key={event.event_id}>
                  <div>
                    <strong>{event.kind}</strong>
                    {event.detail ? ` — ${event.detail}` : ""}
                  </div>
                  <div className="when">
                    {event.at.replace("T", " ").replace("+00:00", " UTC")} · {event.actor}
                  </div>
                </li>
              ))}
            </ul>
          </Panel>

          {canDelete && (
            <Panel title="Danger">
              <button
                className="btn"
                onClick={async () => {
                  if (!confirm(`Delete "${task.title}"? The log goes with it.`)) return;
                  await run(() => api.removeTask(id));
                  await refresh();
                  go("/tasks");
                }}
              >
                Delete this task
              </button>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
